import React from 'react';
import type { Place, Language } from '@/types';
import Colors from '@/constants/colors';

interface PromotedCarouselProps {
  places: Place[];
  language: Language;
  onPlaceClick?: (placeId: string) => void;
}

export default function PromotedCarousel({
  places,
  language,
  onPlaceClick,
}: PromotedCarouselProps) {
  if (!places || places.length === 0) {
    return null;
  }

  const getName = (place: Place) => {
    return language === 'ru' ? place.name_ru || place.name : place.name;
  };

  const getDescription = (place: Place) => {
    const desc = language === 'ru' ? place.description_ru || place.description : place.description;
    return desc?.substring(0, 100) + '...' || '';
  };

  return (
    <div style={styles.container}>
      <div style={styles.carousel}>
        {places.map((place) => (
          <div
            key={place.id}
            style={styles.card}
            onClick={() => onPlaceClick?.(place.id)}
            role="button"
            tabIndex={0}
          >
            <img
              src={place.imageUrl || place.image_url}
              alt={getName(place)}
              style={styles.image}
              loading="lazy"
              decoding="async"
            />
            <div style={styles.overlay} />
            <div style={styles.content}>
              <h3 style={styles.title}>{getName(place)}</h3>
              <p style={styles.description}>{getDescription(place)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
  } as React.CSSProperties,

  carousel: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: '4px',
    // Hide scrollbar but keep functionality
    scrollbarWidth: 'thin',
    scrollbarColor: Colors.border + ' transparent',
  } as React.CSSProperties,

  card: {
    flexShrink: 0,
    width: '280px',
    height: '200px',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    position: 'relative',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  } as React.CSSProperties,

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  } as React.CSSProperties,

  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
  } as React.CSSProperties,

  content: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '16px',
    color: 'white',
  } as React.CSSProperties,

  title: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '700',
    lineHeight: '1.2',
  } as React.CSSProperties,

  description: {
    margin: 0,
    fontSize: '12px',
    lineHeight: '1.4',
    opacity: 0.9,
  } as React.CSSProperties,
};
